import React, { Fragment, useEffect, useState } from "react";
import { Typography, Radio, Slider, ConfigProvider, Button, Tabs, Select, InputNumber, Switch, Space } from 'antd';
import { TwitterPicker } from 'react-color';
import {
    setBackgroundColorSetting, setFontColorSetting, selectBackgroundColorSetting,
    selectSettings, selectFontSettings, setFontSettings, selectFontColorSetting,
    selectCurveOverrides, setCurveOverride, resetCurveOverride,
    selectChartSettings, setChartSetting,
} from "features/petrophysics/petrophysicsSlice";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { CloseCircleOutlined } from "@ant-design/icons";
import { CURVE_DEFAULTS, getCurveDefault, LINE_STYLE_OPTIONS } from "app/logViewerDefaults";
const { Text } = Typography;

const options = [
    { label: 'Well', value: 'well' },
    { label: 'Curve', value: 'curve' },
    { label: 'Scale', value: 'scale' },
];

const labelStyle = { color: 'rgba(255,255,255,0.65)', fontSize: 12, minWidth: 54, display: 'inline-block' };

const PpSettingSelector = (props) => {
    const { title, onClose } = props;

    const settings = useSelector(selectSettings, shallowEqual);
    const fontSetting = useSelector(selectFontSettings, shallowEqual);
    const curveOverrides = useSelector(selectCurveOverrides, shallowEqual);
    const chartSettings = useSelector(selectChartSettings, shallowEqual);

    const [selectedFontItem, setSelectedFontItem] = useState(options[0].value);
    const [fontValue, setFontValue] = useState(0);
    const onChange1 = ({ target: { value } }) => {
        setSelectedFontItem(value);
        setFontValue(fontSetting[value]);
    };

    const handleSlider = (value) => {
        if (!selectedFontItem) return;
        let curValue = {};
        curValue[selectedFontItem] = value;
        dispatch(setFontSettings({ ...fontSetting, ...curValue }));
        setFontValue(value);
    };

    const backgroundColorSetting = useSelector(selectBackgroundColorSetting);
    const fontColorSetting = useSelector(selectFontColorSetting);
    const [showPicker, setShowPicker] = useState(false);
    const [backColor, setBackColor] = useState(backgroundColorSetting);
    const [fontColor, setFontColor] = useState(fontColorSetting);
    const [colorItem, setColorItem] = useState('');
    // currently edited curve (Curve tab)
    const [selectedMnemonic, setSelectedMnemonic] = useState(Object.keys(CURVE_DEFAULTS)[0]);
    const dispatch = useDispatch();

    const curveCfg = getCurveDefault(selectedMnemonic, curveOverrides);

    const updateCurve = (patch) => {
        dispatch(setCurveOverride({ mnemonic: selectedMnemonic, config: patch }));
    };

    useEffect(() => {
        if (!selectedFontItem || !fontSetting) return;
        setFontValue(fontSetting[selectedFontItem]);
    }, [selectedFontItem, fontSetting]);

    useEffect(() => {
        if (settings.color.background === '#000') return;
        localStorage.setItem("localSettings", JSON.stringify(settings));
        setBackColor(settings.color.background);
        setFontColor(settings.color.font);
        if (selectedFontItem) {
            setFontValue(settings.font[selectedFontItem]);
        }
    }, [settings, selectedFontItem]);

    const onPickColor = (color) => {
        if (colorItem === 'font') { setFontColor(color.hex); dispatch(setFontColorSetting(color.hex)); }
        else if (colorItem === 'background') { setBackColor(color.hex); dispatch(setBackgroundColorSetting(color.hex)); }
        else if (colorItem === 'curve') { updateCurve({ color: color.hex }); }
        else if (colorItem === 'grid') { dispatch(setChartSetting({ gridColor: color.hex })); }
        setShowPicker(false);
    };

    const globalTab = (
        <>
            <Text level={5} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '1.3em' }}>Font Size</Text>
            <div style={{ color: 'rgba(255,255,255,0.5)' }}>
                <Radio.Group onChange={onChange1} value={selectedFontItem} style={{ color: '#eee', marginTop: '8px', marginBottom: '5px' }}>
                    {options.map((op, index) => (
                        <Radio key={index} value={op.value} style={{ color: 'rgba(255, 255, 255, 0.5)' }}>{op.label}</Radio>
                    ))}
                </Radio.Group>
            </div>
            <div style={{ width: '100%' }}>
                <Slider min={1} max={5} value={fontValue} defaultValue={3} onChange={handleSlider} />
            </div>

            <Text level={5} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '1.3em' }}>Color</Text>
            <div style={{ textAlign: 'center', marginTop: 6 }}>
                <span style={{ fontSize: 12, color: 'rgba(255, 255, 255,0.5)' }}>
                    Font :
                    <button style={{ width: '24px', height: '24px', backgroundColor: fontColor }}
                        onClick={() => { setColorItem('font'); setShowPicker(!showPicker); }} />
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255, 255, 255,0.5)', marginLeft: '1em' }}>
                    Background :
                    <button style={{ width: '24px', height: '24px', backgroundColor: backColor }}
                        onClick={() => { setColorItem('background'); setShowPicker(!showPicker); }} />
                </span>
            </div>

            <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={labelStyle}>Grid</span>
                <Switch size="small" checked={chartSettings ? chartSettings.showGrid : true}
                    onChange={(v) => dispatch(setChartSetting({ showGrid: v }))} />
                <button style={{ width: 24, height: 24, marginLeft: 12, backgroundColor: (chartSettings && chartSettings.gridColor) || '#888' }}
                    onClick={() => { setColorItem('grid'); setShowPicker(!showPicker); }} title="Grid color" />
            </div>
        </>
    );

    const curveTab = (
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <div>
                <span style={labelStyle}>Curve</span>
                <Select size="small" style={{ width: 130 }} value={selectedMnemonic}
                    onChange={setSelectedMnemonic}
                    options={Object.keys(CURVE_DEFAULTS).map(m => ({ label: m, value: m }))} />
            </div>
            <div>
                <span style={labelStyle}>Color</span>
                <button style={{ width: 24, height: 24, backgroundColor: curveCfg.color, verticalAlign: 'middle' }}
                    onClick={() => { setColorItem('curve'); setShowPicker(!showPicker); }} />
            </div>
            <div>
                <span style={labelStyle}>Width</span>
                <InputNumber size="small" min={0.5} max={5} step={0.5} value={curveCfg.width}
                    onChange={(v) => updateCurve({ width: v })} style={{ width: 70 }} />
            </div>
            <div>
                <span style={labelStyle}>Style</span>
                <Select size="small" style={{ width: 130 }} value={curveCfg.style}
                    onChange={(v) => updateCurve({ style: v })} options={LINE_STYLE_OPTIONS} />
            </div>
            <div>
                <span style={labelStyle}>Scale</span>
                <Radio.Group size="small" value={curveCfg.scale} onChange={(e) => updateCurve({ scale: e.target.value })}>
                    <Radio.Button value="linear">Linear</Radio.Button>
                    <Radio.Button value="log">Log</Radio.Button>
                </Radio.Group>
            </div>
            <div>
                <span style={labelStyle}>Min</span>
                <InputNumber size="small" value={curveCfg.min} onChange={(v) => updateCurve({ min: v })} style={{ width: 80 }} />
                <span style={{ ...labelStyle, marginLeft: 8 }}>Max</span>
                <InputNumber size="small" value={curveCfg.max} onChange={(v) => updateCurve({ max: v })} style={{ width: 80 }} />
            </div>
            <div>
                <span style={labelStyle}>Reverse</span>
                <Switch size="small" checked={!!curveCfg.reverse} onChange={(v) => updateCurve({ reverse: v })} />
                <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginLeft: 8 }}>(high value left)</span>
            </div>
            <Space>
                <Button size="small" danger onClick={() => dispatch(resetCurveOverride(selectedMnemonic))}>Reset this</Button>
                <Button size="small" onClick={() => dispatch(resetCurveOverride())}>Reset all</Button>
            </Space>
        </Space>
    );

    return (
        <Fragment>
            {showPicker && <div className="popup-window" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', zIndex: 200 }}>
                <CloseCircleOutlined style={{ position: 'relative', top: '12px', zIndex: 100 }} onClick={() => setShowPicker(false)} />
                <TwitterPicker
                    color={colorItem === 'font' ? fontColor : colorItem === 'curve' ? curveCfg.color : backColor}
                    colors={['#008000', '#FF0000', '#0000FF', '#000000', '#666666', '#800080', '#FF00FF', '#0070C0', '#FF7F00', '#FFFF00', '#FCB900', '#ABB8C3']}
                    triangle='hide'
                    onChangeComplete={onPickColor}
                    onClose={() => setShowPicker(false)}
                />
            </div>}
            <div className="selector d-flex flex-column w-100 h-100" style={{ alignItems: 'left' }}>
                <ConfigProvider theme={{ components: { Text: { colorPrimary: '#eee' } } }}>
                    <div className="selector-select d-flex flex-column align-items-stretch"
                        style={{ textAlign: 'left', fontSize: '15px', color: 'rgba(255,255,255,0.8)', background: 'rgba(0, 0, 0, 0.2)' }}>
                        {title || 'Settings'}
                    </div>
                    <Tabs
                        size="small"
                        items={[
                            { key: 'global', label: <span style={{ color: '#ddd' }}>Global</span>, children: globalTab },
                            { key: 'curve', label: <span style={{ color: '#ddd' }}>Curve</span>, children: curveTab },
                        ]}
                    />
                    {onClose && <Button size="small" type="primary" style={{ marginTop: '0.5em' }} onClick={() => onClose(false)}>Close</Button>}
                </ConfigProvider>
            </div>
        </Fragment>
    );
};
export default PpSettingSelector;
